import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePendingCount } from '../../hooks/useOfflineSync';
import type { Position } from '../../types';
import type { CargoDayTotal } from '../../services/api';
import { totalsFromRows, type DayTotal } from './monthTotals';
import { getDateLocale } from '../../i18n';
import './MonthCalendarView.css';

function localeTag(lang: string | undefined) {
  return getDateLocale(lang);
}
function dateKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}
function monthKey(year: number, month: number) {
  return `${year}-${month}`;
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Caches module-level: totales por mes (grid), cargas por día (modal) y todas
// las cargas del mes (buscador). Sobreviven a abrir/cerrar y a navegar meses,
// para no repetir peticiones.
const monthTotalsCache = new Map<string, Record<number, DayTotal>>();
const dayPositionsCache = new Map<string, Position[]>();
const monthPositionsCache = new Map<string, Position[]>();

// Clave del buscador del calendario en sessionStorage: sobrevive a ir al
// detalle y volver (igual que el buscador de la lista), para no perder el
// filtro. Se borra al cerrar la pestaña.
const CAL_SEARCH_KEY = 'CALENDAR_SEARCH_QUERY';

export function clearMonthCalendarCache() {
  monthTotalsCache.clear();
  dayPositionsCache.clear();
  monthPositionsCache.clear();
}

// Día del mes (1-31) de una carga, a partir de su fecha. Parseamos el ISO
// directamente para no depender de la zona horaria del navegador.
function dayOfPosition(p: Position): number {
  const s = String(p.date ?? '');
  const m = s.match(/^\d{4}-\d{2}-(\d{2})/);
  if (m) return Number(m[1]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? 0 : d.getDate();
}

// Al cambiar de usuario, la sesión emite este evento → vaciamos las cachés y
// también el texto del buscador (persistido en sessionStorage) para no arrastrar
// la búsqueda del usuario anterior. El componente se remonta tras el login, así
// que arrancará con el buscador en blanco.
if (typeof window !== 'undefined') {
  window.addEventListener('app:clear-caches', () => {
    clearMonthCalendarCache();
    try { sessionStorage.removeItem(CAL_SEARCH_KEY); } catch { /* nada */ }
  });
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Mes/día donde arranca la vista al abrir. */
  initialDate: Date;
  /** Si se indica, al abrir se muestra directamente el modal de ESE día (para
   *  restaurar la vista al volver del detalle). */
  initialSelectedDay?: Date | null;
  /** Totales de contenedores por día del mes (1 sola petición). month es 1-12. */
  loadMonthTotals: (year: number, month: number) => Promise<CargoDayTotal[]>;
  /** Carga las cargas de un día concreto (bajo demanda, al pinchar). */
  loadDay: (date: Date) => Promise<Position[]>;
  /** Todas las cargas del mes con detalle (para el buscador). month es 1-12. */
  loadMonthPositions: (year: number, month: number) => Promise<Position[]>;
  /** Predicado de búsqueda: mismo criterio que la lista (fábrica, controlador,
   *  país, booking…). */
  matchesSearch: (pos: Position, query: string) => boolean;
  /** Filtro extra heredado de la lista (hoy: el interruptor self-managed).
   *  Si viene, los números de cada día se recalculan en el cliente igual que
   *  con el buscador — si no, los totales del backend no cuadrarían con las
   *  cargas que sí se muestran al abrir el día. */
  scopeFilter?: (pos: Position) => boolean;
  /** Render de las cards del día (reutiliza las PositionCard reales). */
  renderDay: (positions: Position[], date: Date) => ReactNode;
  /** Se llama cuando se abre/cierra el modal de un día (para poder restaurar la
   *  vista al volver del detalle). */
  onDaySelected?: (day: Date | null) => void;
  /**
   * Sube cuando el latido de cambios avisa de algo (2026-09-10): el mes y el
   * día abierto se vuelven a leer en silencio, igual que al vaciarse la cola.
   */
  refreshToken?: number;
}

export function MonthCalendarView({ open, onClose, initialDate, initialSelectedDay, loadMonthTotals, loadDay, loadMonthPositions, matchesSearch, scopeFilter, renderDay, onDaySelected, refreshToken = 0 }: Props) {
  const { t, i18n } = useTranslation();
  const locale = localeTag(i18n.language);

  const [view, setView] = useState(() => ({ year: initialDate.getFullYear(), month: initialDate.getMonth() }));
  const [totals, setTotals] = useState<Record<number, DayTotal>>({});
  const [loading, setLoading] = useState(false);

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [dayPositions, setDayPositions] = useState<Position[]>([]);
  const [dayLoading, setDayLoading] = useState(false);

  // Buscador: filtra los números de cada día (y las cargas del modal) por texto.
  // El estado se persiste en sessionStorage para no perderlo al ir al detalle.
  const [search, setSearch] = useState<string>(() => sessionStorage.getItem(CAL_SEARCH_KEY) ?? '');
  const [monthPositions, setMonthPositions] = useState<Position[] | null>(null);
  const [monthPosLoading, setMonthPosLoading] = useState(false);
  const query = search.trim();
  const hasSearch = query.length > 0;
  // Cualquiera de los dos obliga a recalcular en el cliente: los totales
  // agregados del backend no saben ni de texto ni del interruptor.
  const needsMonthRows = hasSearch || !!scopeFilter;
  // Filtro combinado que se aplica en los tres sitios donde se pintan cargas.
  const passesFilters = useCallback(
    (p: Position) => (!scopeFilter || scopeFilter(p)) && matchesSearch(p, query),
    [scopeFilter, matchesSearch, query]
  );

  const today = new Date();

  // Refresco EN VIVO: cuando cambia la cola de pendientes (p.ej. una sync
  // termina), si el calendario está abierto refrescamos en SILENCIO el mes
  // visible (y el día abierto si lo hay) — sin spinner, sin cerrar, sin perder
  // el mes/día en el que estás.
  const pending = usePendingCount();
  const lastPendingRef = useRef(pending);
  const lastRefreshRef = useRef(refreshToken);
  const viewRef = useRef(view);
  viewRef.current = view;
  const selectedDayRef = useRef(selectedDay);
  selectedDayRef.current = selectedDay;

  useEffect(() => {
    const changed = pending !== lastPendingRef.current || refreshToken !== lastRefreshRef.current;
    lastPendingRef.current = pending;
    lastRefreshRef.current = refreshToken;
    if (!(open && changed)) return;
    const v = viewRef.current;
    const mk = monthKey(v.year, v.month);
    loadMonthTotals(v.year, v.month + 1)
      .then((rows) => {
        const map = totalsFromRows(rows);
        monthTotalsCache.set(mk, map);
        setTotals(map);
      })
      .catch(() => {});
    const sd = selectedDayRef.current;
    if (sd) {
      const k = dateKey(sd);
      loadDay(sd)
        .then((positions) => {
          dayPositionsCache.set(k, positions);
          setDayPositions(positions);
        })
        .catch(() => {});
    }
  }, [pending, refreshToken, open, loadMonthTotals, loadDay]);

  // Al abrir, situar la vista en el mes de la fecha seleccionada. Si viene un
  // initialSelectedDay (volver del detalle), abrimos directamente ese día.
  // Apertura NORMAL → vaciamos la caché para traer datos frescos (refleja
  // plannings creadas desde la PWA o el portal). Reapertura al VOLVER del
  // detalle → NO la vaciamos: reutilizamos las cachés module-level (totales,
  // mes y día) para que el retroceso sea instantáneo, sin spinner ni re-fetch.
  useEffect(() => {
    if (open) {
      if (!initialSelectedDay) clearMonthCalendarCache();
      const start = initialSelectedDay ?? initialDate;
      setView({ year: start.getFullYear(), month: start.getMonth() });
      setSelectedDay(initialSelectedDay ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Bloquea el scroll del fondo mientras el calendario está abierto (cubre también
  // el modal del día, que va dentro). Fijamos el body para que en móvil no haga
  // scroll la pantalla de atrás, y restauramos la posición al cerrar.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Escape: primero cierra el modal del día; si no hay, cierra la vista.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setSelectedDay((cur) => {
        if (cur) return null;
        onClose();
        return cur;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Cargar los TOTALES del mes visible (1 petición). Con cache por mes.
  // Con caché: la mostramos AL INSTANTE (sin spinner) y refrescamos en segundo
  // plano, para que al volver del detalle los números salgan ya y se actualicen
  // solos si algo cambió. Sin caché (apertura normal, p.ej. tras crear una
  // planning que vació la caché): spinner + carga fresca.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const mk = monthKey(view.year, view.month);

    const cached = monthTotalsCache.get(mk);
    if (cached) setTotals(cached);
    else { setTotals({}); setLoading(true); }

    loadMonthTotals(view.year, view.month + 1)
      .then((rows) => {
        const map = totalsFromRows(rows);
        monthTotalsCache.set(mk, map);
        if (!cancelled) setTotals(map);
      })
      .catch(() => { if (!cancelled && !cached) setTotals({}); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, view, loadMonthTotals]);

  // Persistir el texto del buscador (sobrevive a ir al detalle y volver).
  useEffect(() => {
    if (search) sessionStorage.setItem(CAL_SEARCH_KEY, search);
    else sessionStorage.removeItem(CAL_SEARCH_KEY);
  }, [search]);

  // Con búsqueda activa cargamos TODAS las cargas del mes (1 petición, cache por
  // mes) para recalcular en el cliente el número de cada día según el filtro.
  // Sin búsqueda no se carga: el grid usa los totales agregados de siempre.
  useEffect(() => {
    if (!open || !needsMonthRows) { setMonthPositions(null); return; }
    let cancelled = false;
    const mk = monthKey(view.year, view.month);
    const cached = monthPositionsCache.get(mk);
    // Con caché: la mostramos AL INSTANTE (sin spinner) y refrescamos en
    // segundo plano para no quedar con datos viejos. Sin caché: spinner + carga.
    if (cached) setMonthPositions(cached);
    else setMonthPosLoading(true);
    loadMonthPositions(view.year, view.month + 1)
      .then((rows) => {
        monthPositionsCache.set(mk, rows);
        if (!cancelled) setMonthPositions(rows);
      })
      .catch(() => { if (!cancelled && !cached) setMonthPositions([]); })
      .finally(() => { if (!cancelled) setMonthPosLoading(false); });
    return () => { cancelled = true; };
  }, [open, needsMonthRows, view, loadMonthPositions]);

  // Cargar las cargas del día seleccionado (bajo demanda). Con cache por día.
  // Si hay búsqueda y las cargas del mes YA están cargadas, no pedimos el día
  // al backend: lo derivamos de monthPositions (instantáneo, sin petición).
  useEffect(() => {
    if (!selectedDay) return;
    if (needsMonthRows && monthPositions) { setDayLoading(false); return; }
    let cancelled = false;
    const k = dateKey(selectedDay);

    const cached = dayPositionsCache.get(k);
    if (cached) {
      setDayPositions(cached);
      setDayLoading(false);
      return;
    }

    setDayPositions([]);
    setDayLoading(true);
    loadDay(selectedDay)
      .then((positions) => {
        dayPositionsCache.set(k, positions);
        if (!cancelled) setDayPositions(positions);
      })
      .catch(() => { if (!cancelled) setDayPositions([]); })
      .finally(() => { if (!cancelled) setDayLoading(false); });

    return () => { cancelled = true; };
  }, [selectedDay, loadDay, needsMonthRows, monthPositions]);

  // Avisar al padre del día abierto (o null al cerrarlo) para poder restaurar la
  // vista al volver del detalle.
  useEffect(() => {
    onDaySelected?.(selectedDay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  // Números bajo cada día: con búsqueda o con el interruptor puesto, se
  // recalculan sobre las cargas del mes que pasan el filtro; sin ninguno de los
  // dos, los totales agregados de siempre.
  const displayTotals = useMemo(() => {
    if (!needsMonthRows || !monthPositions) return totals;
    const map: Record<number, DayTotal> = {};
    for (const p of monthPositions) {
      if (!passesFilters(p)) continue;
      const day = dayOfPosition(p);
      if (day > 0) {
        const cur = map[day] ?? { planned: 0, loaded: 0 };
        map[day] = {
          planned: cur.planned + Number(p.totalContainers || 0),
          loaded: cur.loaded + Number(p.totalContainerLoaded || 0),
        };
      }
    }
    return map;
  }, [needsMonthRows, monthPositions, totals, passesFilters]);

  // Cargas del día abierto, ya filtradas (el modal muestra el resultado
  // filtrado y el filtro se mantiene al entrar/volver del detalle). Con el mes
  // ya cargado se derivan de monthPositions (sin petición del día); si no, de
  // las cargas del día (loadDay).
  const shownDayPositions = useMemo(() => {
    if (!needsMonthRows) return dayPositions;
    if (monthPositions && selectedDay) {
      const d = selectedDay.getDate();
      return monthPositions.filter((p) => dayOfPosition(p) === d && passesFilters(p));
    }
    return dayPositions.filter(passesFilters);
  }, [needsMonthRows, monthPositions, selectedDay, dayPositions, passesFilters]);

  const cells = useMemo(() => {
    const firstOfMonth = new Date(view.year, view.month, 1);
    const leadBlanks = (firstOfMonth.getDay() + 6) % 7; // lunes = 0
    const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
    const arr: (Date | null)[] = [
      ...Array.from({ length: leadBlanks }, () => null),
      ...Array.from({ length: daysInMonth }, (_, i) => new Date(view.year, view.month, i + 1)),
    ];
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [view]);

  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i))); // 2024-01-01 lunes
  }, [locale]);

  const monthTitle = new Date(view.year, view.month, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  const goMonth = (delta: number) =>
    setView((v) => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  const goToday = () => {
    const n = new Date();
    setView({ year: n.getFullYear(), month: n.getMonth() });
  };

  if (!open) return null;

  return (
    <div className="month-cal-overlay" onClick={onClose} data-no-pull-refresh>
      <div className="month-cal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('positions.calendar.title')}>
        <div className="month-cal__header">
          <button className="month-cal__nav" onClick={() => goMonth(-1)} aria-label={t('positions.calendar.prev')}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" /></svg>
          </button>
          <button className="month-cal__title" onClick={goToday} title={t('positions.calendar.today')}>{monthTitle}</button>
          <button className="month-cal__nav" onClick={() => goMonth(1)} aria-label={t('positions.calendar.next')}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" /></svg>
          </button>
          <button className="month-cal__close" onClick={onClose} aria-label={t('positions.calendar.close')}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
          </button>
        </div>

        <div className="month-cal__grid month-cal__grid--head">
          {weekdays.map((w, i) => <span key={i} className="month-cal__weekday">{w}</span>)}
        </div>

        <div className="month-cal__body">
          <div className="month-cal__grid month-cal__grid--days">
            {cells.map((date, i) => {
              if (!date) return <span key={i} className="month-cal__cell month-cal__cell--empty" />;
              const dayTotal = displayTotals[date.getDate()] ?? { planned: 0, loaded: 0 };
              const total = dayTotal.planned;
              // Nada planificado pero algo cargado: se enseña igual, en ámbar y
              // como "cargados/planificados" para que se vea que sobra.
              const overrun = total === 0 && dayTotal.loaded > 0;
              const has = total > 0 || overrun;
              const isToday = sameDay(date, today);
              const badge = overrun ? `${dayTotal.loaded}/0` : String(total);
              return (
                <button
                  key={i}
                  type="button"
                  className={`month-cal__cell${has ? ' month-cal__cell--has' : ''}${overrun ? ' month-cal__cell--overrun' : ''}${isToday ? ' month-cal__cell--today' : ''}`}
                  onClick={() => setSelectedDay(date)}
                  aria-label={`${date.getDate()}${has ? ` · ${badge} ${t('positions.calendar.containers')}` : ''}`}
                >
                  <span className="month-cal__daynum">{date.getDate()}</span>
                  {has && <span className={`month-cal__badge${overrun ? ' month-cal__badge--overrun' : ''}`}>{badge}</span>}
                </button>
              );
            })}
          </div>

          {(loading || monthPosLoading) && (
            <div className="month-cal__spinner-overlay" aria-hidden="true">
              <span className="month-cal__spinner" />
            </div>
          )}
        </div>

        <div className="month-cal__search">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" className="month-cal__search-icon" aria-hidden="true">
            <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 10-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1114 9.5 4.49 4.49 0 019.5 14z" />
          </svg>
          <input
            type="text"
            className="month-cal__search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('positions.searchPlaceholder')}
          />
          {search && (
            <button
              type="button"
              className="month-cal__search-clear"
              onClick={() => setSearch('')}
              aria-label={t('positions.closeSearch')}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
            </button>
          )}
        </div>
      </div>

      {selectedDay && (
        <div className="month-cal-day-overlay" onClick={() => setSelectedDay(null)}>
          <div className="month-cal-day" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="month-cal-day__header">
              <div className="month-cal-day__heading">
                <span className="month-cal-day__title">
                  {selectedDay.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
                </span>
                {!dayLoading && shownDayPositions.length > 0 && (
                  <span className="month-cal-day__count">
                    {t('positions.calendar.loadsCount', { count: shownDayPositions.length })}
                  </span>
                )}
              </div>
              <button className="month-cal-day__close" onClick={() => setSelectedDay(null)} aria-label={t('positions.calendar.close')}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="month-cal-day__body">
              {dayLoading ? (
                <div className="month-cal-day__loading"><span className="month-cal__spinner" /></div>
              ) : shownDayPositions.length > 0 ? (
                renderDay(shownDayPositions, selectedDay)
              ) : (
                <div className="month-cal-day__empty">{t('positions.calendar.noLoads')}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
